/* ============================================================
   Piecewise Starter Templates — Proposal 5B (2026-04-25)

   Each template is a label set for the four built-in engine kinds
   (familyAp / subs / fixedAssets / ltl). The engine logic is the
   same regardless of template; templates only change how the
   categories are LABELED in the UI, so each Book can use language that
   matches its model without changing engine math.

   When users add their own categories beyond the 4 built-in kinds
   (Phase 2 generalization), this file becomes the seed for any
   model stack. For now: pick one of these on first run, then
   refine each label inline via the Piecewise Configurator.
   ============================================================ */

export const PIECEWISE_STARTER_TEMPLATES = [
  {
    id: 'universal-model',
    name: 'Universal Model Stack',
    description:
      'All-encompassing finance, revenue, cost, expense, operations, asset, and liability model labels. Best default for new Books.',
    labels: {
      familyAp:    'Operating obligations',
      subs:        'Recurring lines',
      fixedAssets: 'NC-Assets',
      ltl:         'NC-Liabilities',
    },
    hints: {
      familyAp:    'Recurring obligations, vendor cadence, support cadence, fulfillment cadence, or payable rhythm',
      subs:        'Recurring revenue, costs, subscriptions, operating expenses, retainers, or usage-based lines',
      fixedAssets: 'Non-current assets: property, equipment, vehicles, intangibles, long-lived rights, or capitalized tools',
      ltl:         'Non-current liabilities: term debt, mortgages, leases, notes, or long-horizon obligations',
    },
  },
  {
    id: 'personal-finance',
    name: 'Solo Operator Book',
    description:
      'Support obligations, subscriptions, long-lived assets, and operator-managed liabilities with universal labels.',
    labels: {
      familyAp:    'Support AP',
      subs:        'Subscriptions',
      fixedAssets: 'NC-Assets',
      ltl:         'NC-Liabilities',
    },
    hints: {
      familyAp:    'Recurring support outflows, dependent care, vendor cadence, or other operator obligations',
      subs:        'Software, streaming, memberships, club dues',
      fixedAssets: 'Real estate, vehicles, lifetime software, or other long-lived assets',
      ltl:         'Mortgages, vehicle loans, leases, or long-term operator obligations',
    },
  },
  {
    id: 'smb-service',
    name: 'SMB Service Business',
    description:
      'Service-based small business — consultants, agencies, professional services. Customer deposits replace support AP, equipment replaces real estate.',
    labels: {
      familyAp:    'Customer Deposits',
      subs:        'Software & Tools',
      fixedAssets: 'Equipment',
      ltl:         'Bank Loans',
    },
    hints: {
      familyAp:    'Customer prepayments, retainers, security deposits',
      subs:        'SaaS subscriptions, professional tools, membership dues',
      fixedAssets: 'Computers, vehicles, office equipment, lifetime licenses',
      ltl:         'Term loans, lines of credit, equipment financing',
    },
  },
  {
    id: 'smb-product',
    name: 'SMB Product Business',
    description:
      'Product-based small business — retail, manufacturing, e-commerce. Inventory cost replaces support AP, equipment + warehouses for fixed assets.',
    labels: {
      familyAp:    'Inventory Cost',
      subs:        'Software & Tools',
      fixedAssets: 'Equipment & Property',
      ltl:         'Bank Loans',
    },
    hints: {
      familyAp:    'Inventory purchases, supplier prepayments, raw materials',
      subs:        'SaaS subscriptions, e-commerce platforms, tools',
      fixedAssets: 'Warehouses, machinery, vehicles, office property',
      ltl:         'Working capital loans, equipment financing, mortgages',
    },
  },
];

/** Return the starter template object by id, or undefined. */
export function getStarterTemplate(id) {
  return PIECEWISE_STARTER_TEMPLATES.find((t) => t.id === id);
}

/** The default labels seeded into scenario state on first run. */
export const DEFAULT_PIECEWISE_LABELS = PIECEWISE_STARTER_TEMPLATES[0].labels;

/** Default per-label hints — used as helper text in the configurator. */
export const DEFAULT_PIECEWISE_HINTS = PIECEWISE_STARTER_TEMPLATES[0].hints;

/** The four built-in engine kinds. Templates only relabel these; engine
 *  logic is unchanged. Future Phase 2 will allow users to add categories
 *  beyond these 4 with their own engine semantics. */
export const PIECEWISE_KINDS = ['familyAp', 'subs', 'fixedAssets', 'ltl'];
